import { InfiniteData, QueryClient } from "@tanstack/react-query";
import { LeadTag } from "api/lead-tags";
import { LEADS_PIPELINE_COLUMN_KEY } from "shared/app/LeadsTableView/constants";
import { PaginatedResponse } from "shared/types";
import { Lead } from "views/leads-id/types";

const LEADS_TABLE_KEY = "LEADS_TABLE";
const LEAD_KEY = "LEAD";

type TagsUpdater = (tags: LeadTag[]) => LeadTag[];

const updateLeadInPage = (
  page: PaginatedResponse<Lead>,
  leadId: number,
  update: TagsUpdater
): PaginatedResponse<Lead> =>
  page.items.some((lead) => lead.id === leadId)
    ? {
        ...page,
        items: page.items.map((lead) =>
          lead.id === leadId ? { ...lead, tags: update(lead.tags ?? []) } : lead
        )
      }
    : page;

/**
 * Writes a Lead's tags into every cache that renders them: the Leads table (also
 * embedded on Contact / Property / Unit), the Pipeline columns and the Lead page.
 * Updating in place, rather than refetching, keeps an open tag picker mounted: the
 * table drops its rows while a refetch is in flight.
 */
export const updateLeadTagsInCaches = (
  queryClient: QueryClient,
  leadId: number,
  update: TagsUpdater
): void => {
  queryClient.setQueriesData<PaginatedResponse<Lead>>(
    { queryKey: [LEADS_TABLE_KEY] },
    (data) => (data ? updateLeadInPage(data, leadId, update) : data)
  );
  queryClient.setQueriesData<InfiniteData<PaginatedResponse<Lead>>>(
    { queryKey: [LEADS_PIPELINE_COLUMN_KEY] },
    (data) =>
      data
        ? {
            ...data,
            pages: data.pages.map((page) =>
              updateLeadInPage(page, leadId, update)
            )
          }
        : data
  );
  queryClient.setQueryData<Lead>([LEAD_KEY, String(leadId)], (lead) =>
    lead ? { ...lead, tags: update(lead.tags ?? []) } : lead
  );
};

/**
 * Marks every list that filters or counts by tag as stale without refetching it now,
 * so a filtered list picks the change up on its next load instead of losing rows
 * (and the open picker) mid-edit.
 */
export const markTagDependentListsStale = (queryClient: QueryClient): void => {
  [LEADS_TABLE_KEY, LEADS_PIPELINE_COLUMN_KEY, "LEADS_FILTER_OPTIONS"].forEach(
    (key) =>
      queryClient.invalidateQueries({ queryKey: [key], refetchType: "none" })
  );
};
